<template>
  <canvas
    ref="drawingCanvas"
    class="absolute inset-0 w-full h-full"
    :style="{
      zIndex: 10,
      pointerEvents: isActive ? 'auto' : 'none',
      cursor: isActive ? 'crosshair' : 'default',
      clipPath: eggClipPath,
    }"
    @mousedown="startDrawing"
    @mousemove="draw"
    @mouseup="stopDrawing"
    @mouseleave="stopDrawing"
    @touchstart.prevent="startDrawing"
    @touchmove.prevent="draw"
    @touchend.prevent="stopDrawing"
    @touchcancel.prevent="stopDrawing"
  ></canvas>
</template>

<script>
import { getEggClipPathCSS } from '@/utils/egg-shape';

export default {
  name: 'EggCanvas',
  props: {
    isActive: {
      type: Boolean,
      default: true,
    },
    brushColor: {
      type: String,
      default: '#FF4444',
    },
    brushSize: {
      type: Number,
      default: 5,
    },
    containerWidth: {
      type: Number,
      required: true,
    },
    containerHeight: {
      type: Number,
      required: true,
    },
  },
  emits: ['content-changed'],
  data() {
    return {
      isDrawing: false,
      lastPoint: null,
      strokes: [],
      currentStroke: null,
      hasContent: false,
      eggClipPath: getEggClipPathCSS(),
    };
  },
  watch: {
    containerWidth() {
      this.resizeCanvas();
    },
    containerHeight() {
      this.resizeCanvas();
    },
  },
  mounted() {
    this.resizeCanvas();
  },
  methods: {
    resizeCanvas() {
      const canvas = this.$refs.drawingCanvas;
      if (!canvas) return;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = this.containerWidth * dpr;
      canvas.height = this.containerHeight * dpr;
      canvas.style.width = `${this.containerWidth}px`;
      canvas.style.height = `${this.containerHeight}px`;

      this.replayStrokes();
    },

    getPointerPosition(e) {
      const canvas = this.$refs.drawingCanvas;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      let clientX, clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      } else {
        clientX = e.clientX;
        clientY = e.clientY;
      }

      return {
        x: (clientX - rect.left) * dpr,
        y: (clientY - rect.top) * dpr,
      };
    },

    startDrawing(e) {
      if (!this.isActive) return;

      this.isDrawing = true;
      this.lastPoint = this.getPointerPosition(e);
      this.currentStroke = {
        points: [{ ...this.lastPoint }],
        color: this.brushColor,
        width: this.brushSize * (window.devicePixelRatio || 1),
      };
    },

    draw(e) {
      if (!this.isDrawing || !this.lastPoint) return;

      const currentPoint = this.getPointerPosition(e);
      const canvas = this.$refs.drawingCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = this.currentStroke.color;
      ctx.lineWidth = this.currentStroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(this.lastPoint.x, this.lastPoint.y);
      ctx.lineTo(currentPoint.x, currentPoint.y);
      ctx.stroke();

      this.currentStroke.points.push({ ...currentPoint });
      this.lastPoint = currentPoint;
    },

    stopDrawing() {
      if (!this.isDrawing) return;

      this.isDrawing = false;
      this.lastPoint = null;

      if (this.currentStroke && this.currentStroke.points.length > 1) {
        this.strokes.push(this.currentStroke);
        this.hasContent = true;
        this.$emit('content-changed', true);
      }

      this.currentStroke = null;
    },

    replayStrokes() {
      const canvas = this.$refs.drawingCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const stroke of this.strokes) {
        this.drawStroke(ctx, stroke);
      }
    },

    drawStroke(ctx, stroke) {
      if (stroke.points.length < 2) return;

      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      for (let i = 1; i < stroke.points.length; i++) {
        ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
      }
      ctx.stroke();
    },

    undo() {
      if (this.strokes.length === 0) return;

      this.strokes.pop();
      this.replayStrokes();

      this.hasContent = this.strokes.length > 0;
      this.$emit('content-changed', this.hasContent);
    },

    clear() {
      this.strokes = [];
      const canvas = this.$refs.drawingCanvas;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.hasContent = false;
      this.$emit('content-changed', false);
    },

    getCanvas() {
      return this.$refs.drawingCanvas;
    },
  },
};
</script>
